import React from 'react';
import { useTranslation } from 'react-i18next';
import type { LanguagesAnalysis } from '../../types';

interface LanguagesSectionProps {
  data: LanguagesAnalysis;
}

const LanguagesSection: React.FC<LanguagesSectionProps> = ({ data }) => {
  const { t } = useTranslation();

  const getLanguageEmoji = (language?: string): string => {
    if (!language) return '🌐';
    const languageEmojis: Record<string, string> = {
      'french': '🇫🇷',
      'english': '🇬🇧',
      'spanish': '🇪🇸',
      'german': '🇩🇪',
      'italian': '🇮🇹',
      'chinese': '🇨🇳',
      'japanese': '🇯🇵',
      'portuguese': '🇵🇹',
      'russian': '🇷🇺',
      'arabic': '🇸🇦',
      'hindi': '🇮🇳',
      'dutch': '🇳🇱'
    };
    return languageEmojis[language.toLowerCase()] || '🌐';
  };
  
  const getLevelColor = (level: string): string => {
    if (level.includes('native') || level.includes('bilingual')) return 'bg-green-100 text-green-800 border-green-300';
    if (level.includes('fluent') || level.includes('advanced')) return 'bg-blue-100 text-blue-800 border-blue-300';
    if (level.includes('intermediate')) return 'bg-yellow-100 text-yellow-800 border-yellow-300';
    return 'bg-gray-100 text-gray-800 border-gray-300';
  };

  const getLevelLabel = (level: string): string => {
    switch (level.toLowerCase()) { 
      case 'native':
      case 'bilingual':
        return t('results.languages.level.native');
      case 'fluent':
      case 'advanced':
        return t('results.languages.level.fluent');
      case 'intermediate':
        return t('results.languages.level.intermediate');
      case 'basic':
        return t('results.languages.level.basic');
      default:
        return level;
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden">
      <div className="bg-gradient-to-r from-cyan-600 to-cyan-700 text-white p-5 flex items-center">
        <span className="text-2xl mr-3">🌍</span>
        <h3 className="text-xl font-semibold">{t('results.languages.title')}</h3>
        <span className="ml-auto text-xl font-bold">{data?.score}%</span>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.languages.foundTitle')}</h4>
            
            {data?.found?.length > 0 ? (
              <div className="space-y-3">
                {data?.found?.map((lang, index) => (
                  <div key={index} className="flex items-center p-3 bg-white rounded-lg shadow-sm">
                    <span className="text-2xl mr-3">{getLanguageEmoji(lang?.name)}</span>
                    <div>
                      <div className="font-medium capitalize">{lang?.name}</div>
                      <div className="text-sm text-gray-600">{getLevelLabel(lang?.level)}</div>
                    </div>
                    <div className="ml-auto">
                      <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium border ${getLevelColor(lang?.level)}`}>
                        {getLevelLabel(lang?.level)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600 italic">{t('results.languages.noneFound')}</p>
            )}
          </div>
          
          <div className="bg-gray-50 rounded-lg p-5">
            <h4 className="font-semibold text-lg mb-4 text-gray-800">{t('results.languages.compatibilityTitle')}</h4>
            
            {data?.analysis?.totalRequired > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">{t('results.languages.required')}:</span>
                  <span className="font-medium">{data?.analysis?.totalRequired}</span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">{t('results.languages.found')}:</span>
                  <span className={`font-medium ${data?.analysis?.foundCount === data?.analysis?.totalRequired ? 'text-green-600' : 'text-orange-600'}`}>
                    {data?.analysis?.foundCount}/{data?.analysis?.totalRequired}
                  </span>
                </div>
                
                {data?.matching?.length > 0 && (
                  <div className="pt-2">
                    <h5 className="font-medium text-gray-800 mb-2">{t('results.languages.matching')}</h5>
                    <div className="space-y-2">
                      {data?.matching?.map((match, index) => (
                        <div key={index} className="flex items-center">
                          <span className="text-green-500 mr-2">✓</span>
                          <span>{match?.required} - {match?.found?.level}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {data?.missing?.length > 0 && (
                  <div className="pt-2">
                    <h5 className="font-medium text-gray-800 mb-2">{t('results.languages.missing')}</h5>
                    <div className="space-y-2">
                      {data?.missing?.map((lang, index) => (
                        <div key={index} className="flex items-center">
                          <span className="text-red-500 mr-2">✗</span>
                          <span>{lang}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {data?.analysis?.bilingualBonus && (
                  <div className="bg-blue-50 p-3 rounded-lg mt-4">
                    <div className="font-medium text-blue-700">
                      <span className="mr-2">⭐</span>
                      {t('results.languages.bonus')}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-gray-100 p-3 rounded-lg">
                <p className="text-gray-700">
                  {t('results.languages.noRequirements')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguagesSection;